import { useMemo } from "react";
import { useCheckoutStore } from "@/pages/checkout/stores/use-checkout-store";
import { useCities, useCountries, usePhoneCodes, useStates } from "@/hooks/use-locations";

/**
 * Wires the generic location queries (countries, phone codes, states, cities)
 * to the checkout address store. Handles cascading updates when the user
 * changes country or state, and exposes derived flags for whether the state
 * and city fields should fall back to free-text inputs.
 */
export const useCheckoutLocations = () => {
  const country = useCheckoutStore((s) => s.form.country);
  const citySubdivision1 = useCheckoutStore((s) => s.form.citySubdivision1);
  const updateField = useCheckoutStore((s) => s.updateField);
  const selectCountry = useCheckoutStore((s) => s.selectCountry);
  const selectState = useCheckoutStore((s) => s.selectState);

  const { data: countries = [] } = useCountries();
  const { data: phoneCodes = [] } = usePhoneCodes();

  const matchedCountry = useMemo(
    () => countries.find((c) => c.name === country),
    [countries, country]
  );
  const countryIso2 = matchedCountry?.iso2;

  const { data: states, isFetched: statesFetched } = useStates(countryIso2);
  const { data: cities, isFetched: citiesFetched } = useCities(countryIso2, citySubdivision1 || undefined);

  const useTextInputStates = statesFetched && (!states || states.length === 0);
  const isStateDisabled = !countryIso2 || !statesFetched;

  const useTextInputCities = useTextInputStates || (citiesFetched && (!cities || cities.length === 0));
  const isCityDisabled = !citySubdivision1 || (!useTextInputCities && !citiesFetched);

  const handleCountryChange = (selectedCountryName: string) => {
    const next = countries.find((c) => c.name === selectedCountryName);
    selectCountry(
      selectedCountryName,
      next ? { name: next.name, iso3: next.iso3, phonecode: next.phonecode } : undefined
    );
  };

  const handleStateChange = (selectedStateName: string) => {
    selectState(selectedStateName);
  };

  const handleCityChange = (selectedCity: string) => {
    updateField("city", selectedCity);
  };

  return {
    countries,
    phoneCodes,
    states,
    cities,
    isStateDisabled,
    isCityDisabled,
    useTextInputStates,
    useTextInputCities,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
  };
};
